export type DateLike = Date | string | number;
export type MaybeDateLike = DateLike | null | undefined;

const MS_PER_DAY = 86_400_000;
const UTC_DATETIME_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
});
const UTC_DATE_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function toDate(value: DateLike): Date {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date value');
  }
  return date;
}

export function utcNow(): Date {
  return new Date(Date.now());
}

export function ensureUtcDate(value: DateLike): Date {
  return toDate(value);
}

export function maybeUtcDate(value?: MaybeDateLike): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  try {
    return toDate(value);
  } catch {
    return null;
  }
}

export function addUtcDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

export function utcISOString(value?: MaybeDateLike): string | null {
  const date = maybeUtcDate(value);
  return date ? date.toISOString() : null;
}

export function formatUtcDateTime(value?: MaybeDateLike, emptyValue = '—'): string {
  const date = maybeUtcDate(value);
  return date ? `${UTC_DATETIME_FORMATTER.format(date)} UTC` : emptyValue;
}

export function formatUtcDate(value?: MaybeDateLike, emptyValue = '—'): string {
  const date = maybeUtcDate(value);
  return date ? UTC_DATE_FORMATTER.format(date) : emptyValue;
}

export function isUtcAfter(left: MaybeDateLike, right: MaybeDateLike): boolean {
  const leftDate = maybeUtcDate(left);
  const rightDate = maybeUtcDate(right);
  if (!leftDate || !rightDate) {
    return false;
  }
  return leftDate.getTime() > rightDate.getTime();
}

export function serializeUtcFields<
  T extends Record<string, any>,
  K extends keyof T,
>(
  record: T,
  fields: readonly K[],
): Omit<T, K> & { [P in K]: string | null } {
  const clone = { ...record } as Record<string, unknown>;
  for (const field of fields) {
    clone[field as string] = utcISOString(record[field] as MaybeDateLike);
  }
  return clone as Omit<T, K> & { [P in K]: string | null };
}
